import { Controller } from "@hotwired/stimulus"

// Self-hosted TinyMCE (public/vendor/tinymce — see task notes in that
// directory's origin) wired to a hidden textarea, used by the blog post
// admin form. Not bundled through esbuild: TinyMCE resolves its own
// plugins/skins/themes at runtime via relative fetches from its own
// script src, which works cleanly against a plain static directory but
// not against anything esbuild would fingerprint/tree-shake — so it's
// loaded here as a classic global script instead.
const TINYMCE_BASE = "/vendor/tinymce"

let tinymceLoad = null
function loadTinymce() {
  if (window.tinymce) return Promise.resolve(window.tinymce)
  if (tinymceLoad) return tinymceLoad

  tinymceLoad = new Promise((resolve, reject) => {
    const script = document.createElement("script")
    script.src = `${TINYMCE_BASE}/tinymce.min.js`
    script.onload = () => resolve(window.tinymce)
    script.onerror = reject
    document.head.appendChild(script)
  })
  return tinymceLoad
}

export default class extends Controller {
  static targets = ["textarea"]
  static values = { locale: { type: String, default: "fa" } }

  connect() {
    loadTinymce().then((tinymce) => this.init(tinymce))
  }

  disconnect() {
    window.tinymce?.get(this.textareaTarget.id)?.remove()
  }

  init(tinymce) {
    const rtl = this.localeValue === "fa"
    const textareaId = this.textareaTarget.id

    tinymce.init({
      selector: `#${textareaId}`,
      base_url: TINYMCE_BASE,
      suffix: ".min",
      license_key: "gpl",
      language: rtl ? "fa" : undefined,
      language_url: rtl ? `${TINYMCE_BASE}/langs/fa.js` : undefined,
      directionality: rtl ? "rtl" : "ltr",
      height: 480,
      branding: false,
      promotion: false,
      menubar: false,
      plugins: "advlist autolink autoresize charmap code codesample directionality fullscreen image insertdatetime link lists media nonbreaking preview quickbars searchreplace table visualblocks visualchars wordcount anchor accordion",
      toolbar: "undo redo | blocks | bold italic underline strikethrough | forecolor | link image media table | bullist numlist outdent indent | alignleft aligncenter alignright | blockquote codesample | removeformat | fullscreen preview code",
      quickbars_selection_toolbar: "bold italic | quicklink blockquote",
      autoresize_bottom_margin: 24,
    })

    // TinyMCE hides its UI (visibility:hidden) behind a promise gate
    // while its skin/content CSS and iframe finish loading, then reveals
    // it itself once that resolves — and `tinymce.init()`'s own returned
    // promise resolves on that same gate, so chaining off it wouldn't
    // help if the gate itself hangs. In some browser/environment
    // combinations it does hang indefinitely even though the editor is
    // fully initialized underneath — verified by inspecting the DOM
    // directly: the toolbar, iframe and content are all correctly built,
    // only the reveal never fires. This poll is a plain safety net, not
    // the primary path: it's a no-op as soon as it finds the editor
    // already revealed, which is what happens whenever things work
    // normally.
    this.revealIfStuck(tinymce, textareaId)
  }

  revealIfStuck(tinymce, textareaId) {
    window.setTimeout(() => {
      const editor = tinymce.get(textareaId)
      const container = editor?.getContainer?.()
      // Checked against the inline `style` attribute directly, not
      // getComputedStyle: the stuck state sets `display:none` *and*
      // `visibility:hidden` together, and some environments resolve the
      // computed `visibility` to "visible" regardless (inherited/skin
      // default winning once display:none takes the element out of
      // rendering) even while it's still very much hidden — so
      // getComputedStyle(...).visibility isn't a reliable signal here.
      if (!container || !/display:\s*none/.test(container.getAttribute("style") || "")) return

      container.removeAttribute("style")
      container.removeAttribute("data-mce-style")
      editor.fire("ResizeEditor")
    }, 2000)
  }
}
